import React from 'react'

import { UseEditorStateReturn } from '../hooks/useEditorState'
import { Segment } from '../types'

import { normalizeSegments } from './segmentUtils'

/**
 * Traverses up from a given DOM node to find the corresponding segment ID
 * and returns the segment object and its index from the segments array.
 */
export const getSegmentAndIndexFromNode = (
  node: Node,
  offset: number,
  segments: Segment[],
  editorRef: React.RefObject<HTMLDivElement>,
): { segment: Segment | null; index: number, localPos: number } => {
  let segmentNode: HTMLElement | null = null

  if (node.nodeType === Node.TEXT_NODE) {
    // If the node is a text node, its parent is the element with the dataset.
    segmentNode = node.parentElement
  } else if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as HTMLElement
    if (el.dataset.segmentId) {
      // The node is the segment element itself.
      segmentNode = el
    } else if (editorRef.current?.contains(el)) {
      // If we've clicked on the editor itself or a non-segment element,
      // we need to find the segment based on the character offset.
      let charCount = 0
      for (let i = 0; i < segments.length; i++) {
        const segment = segments[i]
        const segmentLength = segment.content.length
        if (offset >= charCount && offset <= charCount + segmentLength) {
          return { segment, index: i, localPos: offset - charCount }
        }
        charCount += segmentLength
      }
    }
  }

  if (segmentNode?.dataset?.segmentId) {
    const segmentId = segmentNode.dataset.segmentId
    const index = segments.findIndex(s => s.id === segmentId)
    if (index !== -1) {
      return { segment: segments[index], index, localPos: offset }
    }
  }

  return { segment: null, index: -1, localPos: 0 }
}

/**
 * Deletes or replaces the content of a DOM Selection that may span multiple segments.
 *
 */
export const replaceSelection = (
  selection: Selection,
  editorRef: React.RefObject<HTMLDivElement>,
  props: UseEditorStateReturn,
  textToInsert = '',
) => {
  const { segments, setSegments, setActiveIndex, nextCursorPos, setIsNavigating } =
    props

  if (selection.isCollapsed) return

  const { index: anchorIndex, localPos: anchorPos } = getSegmentAndIndexFromNode(
    selection.anchorNode!,
    selection.anchorOffset,
    segments,
    editorRef,
  )
  const { index: focusIndex, localPos: focusPos } = getSegmentAndIndexFromNode(
    selection.focusNode!,
    selection.focusOffset,
    segments,
    editorRef,
  )

  const [startIndex, startOffset, endIndex, endOffset] =
    anchorIndex < focusIndex || (anchorIndex === focusIndex && anchorPos < focusPos)
      ? [anchorIndex, anchorPos, focusIndex, focusPos]
      : [focusIndex, focusPos, anchorIndex, anchorPos]

  if (startIndex === -1 || endIndex === -1) return

  // --- 1. Calculate the absolute cursor position BEFORE any changes ---
  const absoluteCursorPos =
    segments
      .slice(0, startIndex)
      .reduce((sum, s) => sum + s.content.length, 0) +
    startOffset +
    textToInsert.length

  // --- 2. Create the new (un-normalized) segments array ---
  const preNormalizationSegments = [...segments.slice(0, startIndex)]

  const startSegment = segments[startIndex]
  const endSegment = segments[endIndex]
  const beforeText = startSegment.content.slice(0, startOffset)
  const afterText = endSegment.content.slice(endOffset)
  const combinedContent = beforeText + textToInsert + afterText
  const newSegment = { ...startSegment, content: combinedContent }

  const wasMention =
    startSegment.type === 'mention' ||
    (startIndex !== endIndex && endSegment.type === 'mention')
  const contentChanged =
    startIndex !== endIndex || combinedContent !== startSegment.content
  if (wasMention && contentChanged) {
    newSegment.type = 'text'
  }
  preNormalizationSegments.push(newSegment)
  preNormalizationSegments.push(...segments.slice(endIndex + 1))

  // --- 3. Normalize the segments ---
  const normalizedSegments = normalizeSegments(preNormalizationSegments)

  // --- 4. Find the new index and cursor position in the normalized array ---
  let cumulativeLength = 0
  let finalIndex = -1
  let finalOffset = -1

  for (let i = 0; i < normalizedSegments.length; i++) {
    const seg = normalizedSegments[i]
    if (
      absoluteCursorPos >= cumulativeLength &&
      absoluteCursorPos <= cumulativeLength + seg.content.length
    ) {
      finalIndex = i
      finalOffset = absoluteCursorPos - cumulativeLength
      break
    }
    cumulativeLength += seg.content.length
  }
  // Fallback for cursor at the very end
  if (finalIndex === -1) {
    finalIndex = normalizedSegments.length > 0 ? normalizedSegments.length - 1 : 0
    finalOffset = normalizedSegments[finalIndex]?.content.length || 0
  }

  // --- 5. Update state ---
  setSegments(normalizedSegments)
  setActiveIndex(finalIndex)
  nextCursorPos.current = finalOffset
  setIsNavigating(false)
}

/**
 * Finds the segment and local cursor position based on a global character offset.
 */
export const findSegmentFromCursor = (
  pos: number,
  segments: Segment[],
): { segment: Segment; localPos: number } | null => {
  let currentPos = 0
  for (const segment of segments) {
    const segmentEnd = currentPos + segment.content.length
    if (pos >= currentPos && pos < segmentEnd) {
      return { segment, localPos: pos - currentPos }
    }
    // Special case for cursor at the very end of the text
    const totalLength = segments.reduce((sum, s) => sum + s.content.length, 0)
    if (pos === totalLength && pos === segmentEnd) {
      return { segment, localPos: segment.content.length }
    }
    currentPos = segmentEnd
  }
  return null
} 